<template>
  <main class="sf-home">
    <section class="hero">
      <div class="hero-bg">
        <video
          autoplay
          muted
          loop
          playsinline
          :poster="`${cdnUrl}/ai/public/home/home-banner-bg-video.webp`"
        >
          <source
            :src="`${cdnUrl}/ai/public/home/home-banner-bg-video.mp4`"
            type="video/mp4"
          />
        </video>
      </div>
      <div class="hero-inner">
        <div class="hero-badge">
          <span class="pill">NEW</span>
          GPT Image 2 · Seedance 2.0 · Nano Banana 2 — all in one place
        </div>
        <h1>Create Smarter. Faster.<br /><span class="grad">With AI.</span></h1>
        <p class="sub">
          Produce high-quality videos, images, and music effortlessly — one
          platform for every creative idea, right in your browser.
        </p>

        <div class="gen-modes">
          <button
            v-for="(mode, index) in generateModes"
            :key="mode.label"
            type="button"
            class="gen-mode"
            :class="{ active: activeGenerateMode === index }"
            @click="activeGenerateMode = index"
          >
            <UIcon :name="mode.icon" class="gm-ico" />
            {{ mode.label }}
          </button>
        </div>
        <form class="prompt-bar" @submit.prevent="goGenerate">
          <UIcon name="i-lucide-sparkles" class="ico" />
          <input
            v-model="generatePrompt"
            type="text"
            :placeholder="activeMode.placeholder"
          />
          <button class="btn btn-primary" type="submit">Generate</button>
        </form>

        <div class="hero-entries">
          <NuxtLink
            class="hero-chip"
            style="--accent: var(--c-video)"
            to="/ai/video/image-to-video/"
          >
            <span class="dot"></span>Image to Video
          </NuxtLink>
          <NuxtLink
            class="hero-chip"
            style="--accent: var(--c-image)"
            to="/ai/image/text-to-image/"
          >
            <span class="dot"></span>Text to Image
          </NuxtLink>
          <NuxtLink
            class="hero-chip"
            style="--accent: var(--c-audio)"
            to="/ai/audio/text-to-music/"
          >
            <span class="dot"></span>Text to Music
          </NuxtLink>
          <button
            class="hero-chip"
            style="--accent: var(--c-models)"
            type="button"
            @click="scrollToSection('models')"
          >
            <span class="dot"></span>20+ AI Models
          </button>
        </div>

        <div class="trustrow">
          <span>★★★★★ Loved by 2M+ creators</span><span class="sep"></span>
          <span>{{ totalAiToolsCount }} AI tools</span
          ><span class="sep"></span>
          <span>{{ aiModelItems.length }}+ frontier models</span
          ><span class="sep"></span>
          <span>No install — runs in browser</span>
        </div>
      </div>
    </section>

    <section class="block" id="hub">
      <div class="wrap">
        <div class="sec-head">
          <div>
            <span class="eyebrow">Explore the platform</span>
            <h2>One studio, every medium</h2>
            <p class="lead">
              Jump straight into the board you need — image, video, audio,
              frontier models, or free utilities.
            </p>
          </div>
          <NuxtLink class="seemore" to="/ai/effects/"
            >Open the full studio <UIcon name="i-lucide-arrow-right"
          /></NuxtLink>
        </div>

        <div class="hub">
          <div
            v-for="card in hubCards"
            :key="card.title"
            class="hub-card"
            :class="[card.className, { 'has-tags': card.tags?.length }]"
            :style="{ '--accent': card.accent }"
            role="link"
            tabindex="0"
            @click="router.push(card.to)"
            @keydown.enter="router.push(card.to)"
          >
            <span class="glow"></span>
            <div class="hub-ico"><UIcon :name="card.icon" /></div>
            <h3>{{ card.title }}</h3>
            <div class="desc">{{ card.description }}</div>
            <div class="hub-count">{{ card.countText }}</div>
            <div v-if="card.tags?.length" class="hub-tags">
              <NuxtLink
                v-for="tag in card.tags"
                :key="tag.label"
                :to="tag.to"
                @click.stop
              >
                {{ tag.label }}
              </NuxtLink>
            </div>
            <span v-else class="hub-arrow"
              >Explore <UIcon name="i-lucide-arrow-right"
            /></span>
          </div>
        </div>
      </div>
    </section>

    <section class="block" style="padding-top: 0">
      <div class="wrap">
        <div class="sec-head">
          <div>
            <span class="eyebrow">Made with SharkFoto AI</span>
            <h2>Image, video &amp; audio — covered</h2>
          </div>
          <button
            class="seemore"
            type="button"
            @click="scrollToSection('tools')"
          >
            All {{ totalAiToolsCount }} tools
            <UIcon name="i-lucide-arrow-right" />
          </button>
        </div>
        <div class="feature-grid">
          <NuxtLink
            v-for="tool in featuredTools"
            :key="tool.slug"
            class="feat"
            :style="{ '--accent': tool.accent }"
            :to="tool.to"
          >
            <div class="media">
              <video
                v-if="tool.mediaType === 'video'"
                autoplay
                muted
                loop
                playsinline
              >
                <source :src="tool.media" type="video/mp4" />
              </video>
              <img v-else :src="tool.media" :alt="tool.title" loading="lazy" />
            </div>
            <div class="body">
              <div class="tag">
                <span class="dot"></span>{{ tool.typeLabel }}
              </div>
              <h3>{{ tool.title }}</h3>
              <p>{{ tool.description }}</p>
              <span class="go"
                >Try {{ tool.title }} <UIcon name="i-lucide-arrow-right"
              /></span>
            </div>
          </NuxtLink>
        </div>
      </div>
    </section>

    <section class="block" id="effects" style="padding-top: 0">
      <div class="wrap">
        <div class="sec-head">
          <div>
            <span class="eyebrow" style="color: var(--brand)">AI Effect</span>
            <h2>One-tap effects that go viral</h2>
            <p class="lead">
              Pick a trending template, drop in a photo, and let AI do the rest
              — no prompts, no editing.
            </p>
          </div>
          <NuxtLink class="seemore" to="/ai/effects/"
            >Browse all effects <UIcon name="i-lucide-arrow-right"
          /></NuxtLink>
        </div>
        <div class="fx-group">
          <div class="fx-grouphead">
            <h3>
              <span class="fx-badge" style="background: var(--c-video)"
                ><UIcon name="i-lucide-video" /></span
              >Video Effects
            </h3>
            <NuxtLink class="fx-more" to="/ai/effects/?type=video"
              >View all video effects <UIcon name="i-lucide-arrow-right"
            /></NuxtLink>
          </div>
          <div class="fx-rail">
            <NuxtLink
              v-for="effect in videoEffects"
              :key="effect.code"
              class="fx-card"
              :style="{
                '--accent': effect.tagColor,
                '--cover-ratio': effect.aspectRatio,
                '--fx-tag-color': effect.tagColor,
              }"
              :to="effect.to"
            >
              <span class="fx-bg"><span class="fx-grain"></span></span>
              <video
                v-if="effect.media && effect.mediaType === 'video'"
                class="fx-media"
                :src="effect.media"
                autoplay
                muted
                loop
                playsinline
              />
              <img
                v-else-if="effect.media"
                class="fx-media"
                :src="effect.media"
                :alt="effect.title"
                loading="lazy"
              />
              <span class="fx-shade"></span>
              <span class="fx-glyph"><UIcon name="i-lucide-sparkles" /></span>
              <span class="fx-body">
                <span class="fx-tag">{{ effect.categoryLabel }}</span>
                <h4>{{ effect.title }}</h4>
                <span class="fx-try"
                  >Try effect <UIcon name="i-lucide-arrow-right"
                /></span>
              </span>
            </NuxtLink>
          </div>
        </div>
        <div class="fx-group">
          <div class="fx-grouphead">
            <h3>
              <span class="fx-badge" style="background: var(--c-image)"
                ><UIcon name="i-lucide-image" /></span
              >Image Effects
            </h3>
            <NuxtLink class="fx-more" to="/ai/effects/?type=image"
              >View all image effects <UIcon name="i-lucide-arrow-right"
            /></NuxtLink>
          </div>
          <div class="fx-rail">
            <NuxtLink
              v-for="effect in imageEffects"
              :key="effect.code"
              class="fx-card"
              :style="{
                '--accent': effect.tagColor,
                '--cover-ratio': effect.aspectRatio,
                '--fx-tag-color': effect.tagColor,
              }"
              :to="effect.to"
            >
              <span class="fx-bg"><span class="fx-grain"></span></span>
              <video
                v-if="effect.media && effect.mediaType === 'video'"
                class="fx-media"
                :src="effect.media"
                autoplay
                muted
                loop
                playsinline
              />
              <img
                v-else-if="effect.media"
                class="fx-media"
                :src="effect.media"
                :alt="effect.title"
                loading="lazy"
              />
              <span class="fx-shade"></span>
              <span class="fx-glyph"><UIcon name="i-lucide-sparkles" /></span>
              <span class="fx-body">
                <span class="fx-tag">{{ effect.categoryLabel }}</span>
                <h4>{{ effect.title }}</h4>
                <span class="fx-try"
                  >Try effect <UIcon name="i-lucide-arrow-right"
                /></span>
              </span>
            </NuxtLink>
          </div>
        </div>
      </div>
    </section>

    <section class="block" id="tools" style="padding-top: 0">
      <div class="wrap">
        <div class="sec-head">
          <div>
            <span class="eyebrow">Popular AI tools</span>
            <h2>The tools creators reach for most</h2>
          </div>
        </div>
        <div class="tools-grid">
          <NuxtLink
            v-for="tool in popularTools"
            :key="tool.to"
            class="tool"
            :to="tool.to"
          >
            <div class="thumb">
              <img
                v-if="tool.image"
                :src="tool.image"
                :alt="tool.title"
                loading="lazy"
              />
              <div v-else class="thumb-fallback">
                <UIcon :name="tool.icon" />
              </div>
            </div>
            <div class="t-body">
              <h4>{{ tool.title }}</h4>
              <p>{{ tool.description }}</p>
              <span class="try"
                >Try for free <UIcon name="i-lucide-arrow-right"
              /></span>
            </div>
          </NuxtLink>
        </div>
      </div>
    </section>

    <section class="block models-wrap" id="models">
      <div class="wrap">
        <div class="sec-head">
          <div>
            <span class="eyebrow" style="color: var(--c-models)"
              >Frontier models</span
            >
            <h2>20+ AI models, one subscription</h2>
            <p class="lead">
              Switch between the world's best image, video and audio models
              without juggling accounts.
            </p>
          </div>
          <NuxtLink
            class="seemore"
            to="/ai/models/"
            style="border-color: var(--c-models)"
            >Browse all models <UIcon name="i-lucide-arrow-right"
          /></NuxtLink>
        </div>
        <div class="model-tabs">
          <button
            v-for="group in modelGroupsForHome"
            :key="group.key"
            type="button"
            class="model-tab"
            :class="{ active: activeModelGroupKey === group.key }"
            @click="activeModelGroupKey = group.key"
          >
            {{ group.title }}
          </button>
        </div>
        <div
          v-for="group in modelGroupsForHome"
          :key="group.key"
          class="model-pane"
          :class="{ active: activeModelGroupKey === group.key }"
        >
          <div class="model-grid">
            <NuxtLink
              v-for="model in group.models"
              :key="model.to"
              class="model-card"
              :to="model.to"
            >
              <span class="model-logo">
                <img
                  v-if="model.avatar"
                  :src="model.avatar"
                  :alt="model.label"
                  loading="lazy"
                />
                <span v-else>{{ model.label.charAt(0) }}</span>
              </span>
              <span class="model-meta">
                <span class="name">{{ model.label }}</span>
                <span class="prov">{{ model.vendor || "AI Model" }}</span>
              </span>
            </NuxtLink>
          </div>
        </div>
      </div>
    </section>

    <section class="block" id="free" style="padding-bottom: 0">
      <div class="wrap">
        <div class="sec-head">
          <div>
            <span class="eyebrow" style="color: var(--c-free)"
              >Free · no sign-up</span
            >
            <h2>Handy image utilities, on the house</h2>
          </div>
        </div>
        <div class="free-strip">
          <NuxtLink
            v-for="tool in freeTools"
            :key="tool.to"
            class="free-card"
            :to="tool.to"
          >
            <span class="free-ico"><UIcon :name="tool.icon" /></span>
            <span
              ><span class="fn">{{ tool.label }}</span
              ><br /><span class="fb">{{ tool.description }}</span></span
            >
          </NuxtLink>
        </div>
      </div>
    </section>

    <section class="block">
      <div class="wrap">
        <div class="sec-head" style="margin-bottom: 30px">
          <div>
            <span class="eyebrow">Built for the way you create</span>
            <h2>Making personalization easy for all</h2>
          </div>
        </div>
        <div class="uc-tabs">
          <button
            v-for="useCase in useCases"
            :key="useCase.title"
            type="button"
            class="uc-tab"
            :class="{ active: activeUseCase === useCase.title }"
            @click="activeUseCase = useCase.title"
          >
            {{ useCase.title }}
          </button>
        </div>
        <div
          v-for="useCase in useCases"
          :key="useCase.title"
          class="uc-pane"
          :class="{ active: activeUseCase === useCase.title }"
        >
          <div class="uc">
            <div>
              <h3>{{ useCase.title }}</h3>
              <div class="tagline">{{ useCase.tagline }}</div>
              <div
                v-for="feature in useCase.features"
                :key="feature.number"
                class="uc-feat"
              >
                <div class="n">{{ feature.number }}</div>
                <div class="ft">
                  <h4>{{ feature.title }}</h4>
                  <p>{{ feature.description }}</p>
                </div>
              </div>
              <NuxtLink
                class="btn btn-line"
                style="margin-top: 24px"
                :to="useCase.to"
                >Try for free</NuxtLink
              >
            </div>
            <div class="uc-visual">
              <img :src="useCase.image" :alt="useCase.title" loading="lazy" />
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="block" style="padding-top: 0">
      <div class="wrap">
        <div class="sec-head">
          <div>
            <span class="eyebrow">Loved by creators</span>
            <h2>Bringing ideas to life with AI</h2>
          </div>
        </div>
        <div class="tcols">
          <div v-for="item in testimonials" :key="item.name" class="tcard">
            <div class="stars">★★★★★</div>
            <p>{{ item.quote }}</p>
            <div class="who">
              <span class="av" :style="{ background: item.color }">{{
                item.name.charAt(0)
              }}</span>
              <span
                ><span class="nm">{{ item.name }}</span
                ><br /><span class="rl">{{ item.role }}</span></span
              >
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="block" style="padding-top: 0">
      <div class="wrap">
        <div class="cta">
          <div class="cta-in">
            <h2>
              Your in-browser AI studio<br />for images, video &amp; sound
            </h2>
            <p>
              Sign up free and get <strong>10 credits</strong> to start — no
              installs, no credit card, no watermarks on paid plans.
            </p>
            <div class="cta-actions">
              <button
                class="btn btn-primary btn-lg"
                type="button"
                @click="handleFreeCreditsClick"
              >
                Get 10 Free Credits
              </button>
              <NuxtLink class="btn btn-line btn-lg" to="/pricing/"
                >View Pricing</NuxtLink
              >
            </div>
          </div>
        </div>
      </div>
    </section>
  </main>
</template>

<script setup>
import { setRefferId } from "@/common/cookies";
import { normalizeEffectsList } from "@/composables/useAiR2ModelsAndEffects";
import { resolvePublicR2Url } from "@/utils/r2Url";
import {
  createProductIndexes,
  enabledModels,
  findMenuGroup,
  modelGroups,
  menuGroupsForArea,
  productForMenuItem,
  toInternalRoute,
} from "@/utils/aiCatalogData";
import { computed, inject, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";

definePageMeta({ layout: "sign-new" });
defineOptions({ name: "home-page" });

const title =
  "SharkFoto – All-in-One AI Media Platform for Videos, Images & Audio Creation | SharkFoto";
const description =
  "Create, edit, and enhance videos, images, and audio instantly with SharkFoto's powerful AI tools. One platform for all your creative media — right in your browser.";
const cdnUrl = useRuntimeConfig().public.cdnUrl;
useHead({
  title: title,
  link: [
    {
      rel: "canonical",
      href: "https://sharkfoto.com",
    },
  ],
  meta: [
    { hid: "description", name: "description", content: description },
    { hid: "og:site_name", property: "og:site_name", content: "SharkFoto" },
    { hid: "og:type", property: "og:type", content: "website" },
    { hid: "og:locale", property: "og:locale", content: "en_US" },
    {
      hid: "og:image",
      property: "og:image",
      content: `${cdnUrl}/sharkfoto_og.png`,
    },
    { hid: "og:url", property: "og:url", content: "https://sharkfoto.com" },
    { hid: "og:title", property: "og:title", content: title },
    { hid: "og:description", property: "og:description", content: description },
    {
      hid: "twitter:card",
      name: "twitter:card",
      content: "summary_large_image",
    },
    {
      hid: "twitter:url",
      name: "twitter:url",
      content: "https://sharkfoto.com",
    },
    { hid: "twitter:title", name: "twitter:title", content: title },
    {
      hid: "twitter:image",
      name: "twitter:image",
      content: `${cdnUrl}/sharkfoto_og.png`,
    },
    { hid: "twitter:site", name: "twitter:site", content: "@Shark_Foto" },
    {
      hid: "twitter:description",
      name: "twitter:description",
      content: description,
    },
  ],
});

const store = useStore();
const router = useRouter();
const route = useRoute();
const menuData = inject("menuData", ref([]));
const productList = inject("productList", ref([]));
const modelList = inject("modelList", ref([]));
const r2CachedFetch = useR2CachedFetch();
const token = computed(() => store.state.user.token);
const { openSignUpModal } = useAuthModals();

onMounted(() => {
  const reffer = route.query?.reffer;
  if (reffer) setRefferId(reffer);
});

const { data: videoEffectsRaw } = useAsyncData(
  "home-video-effects-index",
  async () => {
    try {
      return await r2CachedFetch("/api/r2/ai/effects/video/index.json");
    } catch {
      return [];
    }
  },
  { default: () => [], lazy: true, server: false }
);
const { data: imageEffectsRaw } = useAsyncData(
  "home-image-effects-index",
  async () => {
    try {
      return await r2CachedFetch("/api/r2/ai/effects/image/index.json");
    } catch {
      return [];
    }
  },
  { default: () => [], lazy: true, server: false }
);
const generateModes = [
  {
    label: "Image",
    placeholder: "Describe the image you want to create…",
    to: "/ai/image/text-to-image/",
    icon: "i-lucide-image",
  },
  {
    label: "Video",
    placeholder: "Describe the video you want to create…",
    to: "/ai/video/text-to-video/",
    icon: "i-lucide-video",
  },
  {
    label: "Music",
    placeholder: "Describe the song or mood to create…",
    to: "/ai/audio/text-to-music/",
    icon: "i-lucide-music",
  },
];
const activeGenerateMode = ref(0);
const generatePrompt = ref("");
const activeMode = computed(
  () => generateModes[activeGenerateMode.value] || generateModes[0]
);

function goGenerate() {
  const query = generatePrompt.value.trim()
    ? { prompt: generatePrompt.value.trim() }
    : undefined;
  router.push({ path: activeMode.value.to, query });
}

const headerMenuGroups = computed(() =>
  menuGroupsForArea(menuData.value, "header")
);
const asideMenuGroups = computed(() =>
  menuGroupsForArea(menuData.value, "aside")
);
const productIndexes = computed(() => createProductIndexes(productList.value));
const enabledProducts = computed(() =>
  (Array.isArray(productList.value) ? productList.value : [])
    .filter((product) => product?.enabled !== false)
    .sort(
      (a, b) =>
        Number(a?.sortIndex ?? 9999) - Number(b?.sortIndex ?? 9999) ||
        String(a?.title || a?.toolName || "").localeCompare(
          String(b?.title || b?.toolName || "")
        )
    )
);
const productsByToolName = computed(() => {
  const map = new Map();
  for (const product of enabledProducts.value) {
    if (product?.toolName) map.set(String(product.toolName), product);
  }
  return map;
});
function toolsByGroup(group) {
  const menuGroup = findMenuGroup(headerMenuGroups.value, group);
  return menuGroup?.children || [];
}

const imageTools = computed(() => toolsByGroup("Image AI"));
const videoTools = computed(() => toolsByGroup("Video AI"));
const audioTools = computed(() => toolsByGroup("Audio AI"));
const aiModelItems = computed(() =>
  enabledModels(modelList.value).map((model) => ({
    raw: model,
    label: model.modelName || model.title,
    description: model.description,
    vendor: model.vendor,
    avatar: model?.avatar?.src || "",
    to: toInternalRoute(model.url),
    groups: Array.isArray(model.groups) ? model.groups : [],
  }))
);
const totalAiToolsCount = computed(
  () =>
    imageTools.value.length + videoTools.value.length + audioTools.value.length
);

const hubCards = computed(() => [
  {
    title: "Image AI",
    description:
      "Generate, restyle, upscale, colorize and clean up images in seconds.",
    countText: `${imageTools.value.length} tools`,
    to: "/ai/image/text-to-image/",
    icon: "i-lucide-image",
    accent: "var(--c-image)",
    className: "big",
  },
  {
    title: "Video AI",
    description: "Turn text or stills into cinematic motion.",
    countText: `${videoTools.value.length} tools`,
    to: "/ai/video/image-to-video/",
    icon: "i-lucide-video",
    accent: "var(--c-video)",
    className: "sm",
  },
  {
    title: "Audio AI",
    description: "Compose music & turn lyrics into songs.",
    countText: `${audioTools.value.length} tools`,
    to: "/ai/audio/text-to-music/",
    icon: "i-lucide-music",
    accent: "var(--c-audio)",
    className: "sm",
  },
  {
    title: "AI Models",
    description:
      "Pick the best frontier model for the job — Sora 2, Kling, Veo, Nano Banana, Seedream & more.",
    countText: `${aiModelItems.value.length}+ models`,
    to: "/ai/models/",
    icon: "i-lucide-box",
    accent: "var(--c-models)",
    className: "tall",
    tags: [
      { label: "Sora 2", to: "/ai/models/openai-sora-2/" },
      { label: "Nano Banana 2", to: "/ai/models/google-nano-banana-2/" },
      { label: "Kling O3", to: "/ai/models/kuaishou-kling-o3/" },
    ],
  },
  {
    title: "Free Tools",
    description:
      "Quick image utilities — convert, compress, resize, flip & rotate. No sign-up needed.",
    countText: "5 free tools",
    to: "/free-tools/image/converter/",
    icon: "i-lucide-blocks",
    accent: "var(--c-free)",
    className: "tall",
    tags: [
      { label: "Converter", to: "/free-tools/image/converter/" },
      { label: "Compressor", to: "/free-tools/image/compressor/" },
      { label: "Resizer", to: "/free-tools/image/resize-image/" },
    ],
  },
]);

const featuredSlugs = ["image-to-video", "text-to-image", "lyrics-to-song"];
const featuredSlugTypeNames = {
  "image-to-video": "Video AI",
  "text-to-image": "Image AI",
  "lyrics-to-song": "Audio AI",
};

function accentForTypeName(typeName) {
  const map = {
    "Image AI": "var(--c-image)",
    "Video AI": "var(--c-video)",
    "Audio AI": "var(--c-audio)",
  };
  return map[typeName] || "var(--brand)";
}

function mediaTypeFromUrl(url) {
  return /\.mp4($|\?)/i.test(String(url || "")) ? "video" : "image";
}

const featuredTools = computed(() =>
  featuredSlugs.map((slug) => {
    const product = productsByToolName.value.get(slug) || {};
    const typeName = product.typeName || featuredSlugTypeNames[slug] || "";
    const media = resolvePublicR2Url(product.coverUrl || "");
    return {
      slug,
      title: product.title || slug,
      description: product.description || "",
      to: toInternalRoute(product.url || ""),
      mediaType: mediaTypeFromUrl(media),
      media,
      accent: accentForTypeName(typeName),
      typeLabel: typeName || "AI Tool",
    };
  })
);

function normalizeEffectAspectRatio(value, type) {
  const ratio = Number(value);
  if (Number.isFinite(ratio) && ratio > 0) return ratio;
  return type === "video" ? 0.75 : 1.33;
}

const FX_TAG_COLORS = [
  "#ef4444",
  "#f97316",
  "#eab308",
  "#22c55e",
  "#14b8a6",
  "#06b6d4",
  "#3b82f6",
  "#6366f1",
  "#8b5cf6",
  "#d946ef",
  "#ec4899",
  "#f43f5e",
  "#ff6f9c",
  "#4ea2ff",
  "#34d399",
];

function hashString(value) {
  const s = String(value ?? "");
  let hash = 0;
  for (let i = 0; i < s.length; i += 1) {
    hash = (hash * 31 + s.charCodeAt(i)) >>> 0;
  }
  return hash;
}

function tagColorForCategory(label) {
  const idx = hashString(label) % FX_TAG_COLORS.length;
  return FX_TAG_COLORS[idx];
}

function normalizeEffect(effect, type) {
  const media = resolvePublicR2Url(
    effect?.cover_gif || effect?.cover_video || effect?.cover_url || ""
  );
  const category =
    effect?.category_name ||
    (Array.isArray(effect?.category_names) ? effect.category_names[0] : "") ||
    effect?.template_category ||
    "Effect";
  const categoryLabel = String(category).toUpperCase();
  return {
    ...effect,
    title: effect?.title || effect?.code || "AI Effect",
    media,
    mediaType: /\.mp4($|\?)/i.test(media) ? "video" : "image",
    aspectRatio: normalizeEffectAspectRatio(effect?.cover_aspect_ratio, type),
    categoryLabel,
    tagColor: tagColorForCategory(categoryLabel),
    to: `/ai/effects/${effect?.effect_type || type}/${effect?.code}/`,
  };
}

function indexEffects(raw, type) {
  return normalizeEffectsList(raw)
    .filter((effect) => effect?.enabled !== 0 && effect?.code)
    .slice(0, 8)
    .map((effect) => normalizeEffect(effect, type));
}

const videoEffects = computed(() =>
  indexEffects(videoEffectsRaw.value, "video")
);
const imageEffects = computed(() =>
  indexEffects(imageEffectsRaw.value, "image")
);

const popularTools = computed(() => {
  const excluded = new Set(featuredSlugs);
  const groupConfig = [
    { title: "Image AI", count: 4, icon: "i-lucide-image" },
    { title: "Video AI", count: 3, icon: "i-lucide-video" },
    { title: "Audio AI", count: 2, icon: "i-lucide-music" },
  ];

  return groupConfig.flatMap(({ title, count, icon }) => {
    const group = findMenuGroup(asideMenuGroups.value, title);
    const tools = [];
    for (const item of group?.children || []) {
      const product = productForMenuItem(item, productIndexes.value);
      const toolName = product?.toolName || item.ref;
      if (!toolName || excluded.has(String(toolName))) continue;
      tools.push({
        title: product?.title || item.title,
        description: product?.description || "",
        to: toInternalRoute(product?.url || item.url),
        image: resolvePublicR2Url(product?.coverUrl || ""),
        icon,
      });
      if (tools.length >= count) break;
    }
    return tools;
  });
});

const modelGroupsForHome = computed(() =>
  modelGroups(modelList.value).map((group) => ({
    key: String(group.id ?? group.title),
    title: formatModelGroupTitle(group.title),
    models: group.models.slice(0, 8).map((model) => ({
      raw: model,
      label: model.modelName || model.title,
      description: model.description,
      vendor: model.vendor,
      avatar: model?.avatar?.src || "",
      to: toInternalRoute(model.url),
      groups: Array.isArray(model.groups) ? model.groups : [],
    })),
  }))
);

function formatModelGroupTitle(title) {
  const raw = String(title || "").trim();
  if (!raw) return "AI Models";
  return /models$/i.test(raw) ? raw : `${raw} Models`;
}
const activeModelGroupKey = ref("");
watch(
  modelGroupsForHome,
  (groups) => {
    if (!groups.some((group) => group.key === activeModelGroupKey.value)) {
      activeModelGroupKey.value = groups[0]?.key || "";
    }
  },
  { immediate: true }
);

const freeTools = [
  {
    label: "Image Converter",
    description: "JPG · PNG · WebP",
    icon: "i-lucide-repeat-2",
    to: "/free-tools/image/converter/",
  },
  {
    label: "Image Compressor",
    description: "Shrink file size",
    icon: "i-lucide-minimize-2",
    to: "/free-tools/image/compressor/",
  },
  {
    label: "Image Resizer",
    description: "Any dimension",
    icon: "i-lucide-scan",
    to: "/free-tools/image/resize-image/",
  },
  {
    label: "Image Flipper",
    description: "Mirror H / V",
    icon: "i-lucide-flip-horizontal",
    to: "/free-tools/image/flip-image/",
  },
  {
    label: "Image Rotator",
    description: "Rotate any angle",
    icon: "i-lucide-rotate-cw",
    to: "/free-tools/image/rotate-image/",
  },
];

const useCases = [
  {
    title: "Content Creation",
    tagline: "Where ideas meet AI magic",
    to: "/ai/effects/",
    image: `${cdnUrl}/ai/public/home/content-creation.webp`,
    features: [
      {
        number: "01",
        title: "Trend Radar",
        description:
          "Discover trending visuals, videos and sounds before they go viral.",
      },
      {
        number: "02",
        title: "Prompt Playground",
        description:
          "Turn any concept into eye-catching content — remix, enhance and add your touch.",
      },
      {
        number: "03",
        title: "All-in-One Studio",
        description:
          "Edit, polish and launch with everything you need in one place.",
      },
    ],
  },
  {
    title: "Fun & Lifestyle",
    tagline: "Play, share, express",
    to: "/ai/effects/",
    image: `${cdnUrl}/ai/public/home/fun-lifestyle.webp`,
    features: [
      {
        number: "01",
        title: "Face Swap & Filters",
        description: "Swap faces and try playful AI looks for posts and reels.",
      },
      {
        number: "02",
        title: "Photo Restore",
        description: "Colorize and revive old memories in stunning detail.",
      },
      {
        number: "03",
        title: "Make Your Anthem",
        description: "Turn an inside joke or mood into a full original song.",
      },
    ],
  },
  {
    title: "Commercial Marketing",
    tagline: "Ship campaigns faster",
    to: "/ai/effects/",
    image: `${cdnUrl}/ai/public/home/commercial-marketing.webp`,
    features: [
      {
        number: "01",
        title: "Product Photography",
        description: "Generate studio-grade product shots without a shoot.",
      },
      {
        number: "02",
        title: "Video Ads",
        description: "Spin stills into scroll-stopping motion ads in minutes.",
      },
      {
        number: "03",
        title: "On-Brand at Scale",
        description:
          "Keep colors, style and tone consistent across every asset.",
      },
    ],
  },
];
const activeUseCase = ref(useCases[0].title);

const testimonials = [
  {
    name: "Emma L.",
    role: "Social Media Manager",
    color: "#ff6f9c",
    quote:
      "SharkFoto helped me produce stunning social media visuals in minutes! The AI tools are intuitive, fast and incredibly creative.",
  },
  {
    name: "David K.",
    role: "Indie Musician",
    color: "#22d3ee",
    quote:
      "I can now turn my lyrics into full songs without any musical background. Lyrics to Song is a game-changer!",
  },
  {
    name: "Sophia R.",
    role: "Marketing Specialist",
    color: "#4ea2ff",
    quote:
      "The Image to Video tool blew me away. My static images now feel alive and cinematic.",
  },
  {
    name: "Alex P.",
    role: "Freelance Designer",
    color: "#8b5cf6",
    quote:
      "Background Remover and Color Enhancer saved me hours of editing time. The AI does all the hard work.",
  },
  {
    name: "Mia C.",
    role: "Content Creator",
    color: "#fbbf24",
    quote:
      "From video ads to personalized content, SharkFoto covers everything I need to stay creative.",
  },
  {
    name: "Liam T.",
    role: "Digital Entrepreneur",
    color: "#4ea2ff",
    quote:
      "The workflow is seamless — create, edit and download everything in one place. No other tool compares.",
  },
];

function handleFreeCreditsClick() {
  if (!token.value) {
    openSignUpModal();
    return;
  }
  router.push("/my/earn-credits/");
}

function scrollToSection(id) {
  if (typeof window === "undefined") return;
  const el = document.getElementById(id);
  if (!el) return;
  const container =
    el.closest(".right-scroll") || document.querySelector(".right-scroll");
  const stickyHeader = document.querySelector(".sticky.top-0");
  const offset = stickyHeader
    ? Math.ceil(stickyHeader.getBoundingClientRect().height)
    : 0;
  if (container) {
    const containerRect = container.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    container.scrollTo({
      top: elRect.top - containerRect.top + container.scrollTop - offset,
      behavior: "smooth",
    });
    return;
  }
  window.scrollTo({
    top: el.getBoundingClientRect().top + window.scrollY - offset,
    behavior: "smooth",
  });
}
</script>

<style scoped>

.sf-home {
  --bg: #07080c;
  --bg-2: #0c0e14;
  --surface: #10131b;
  --surface-2: #161a24;
  --line: rgba(255, 255, 255, 0.09);
  --line-strong: rgba(255, 255, 255, 0.16);
  --tx: #f4f6fa;
  --tx-dim: rgba(244, 246, 250, 0.6);
  --tx-faint: rgba(244, 246, 250, 0.42);
  --c-image: #ff6f9c;
  --c-video: #4ea2ff;
  --c-audio: #22d3ee;
  --c-models: #34d399;
  --c-free: #fbbf24;
  --brand: #8b5cf6;
  --brand-2: #b06bfb;
  --radius: 18px;
  --maxw: 1240px;
  overflow-x: hidden;
  color: var(--tx);
  font-family: "Public Sans", system-ui, sans-serif;
  line-height: 1.5;
}

.sf-home a {
  color: inherit;
  text-decoration: none;
}

.sf-home h1,
.sf-home h2,
.sf-home h3,
.sf-home h4 {
  font-family: "Space Grotesk", system-ui, sans-serif;
  font-weight: 600;
  letter-spacing: -0.02em;
  line-height: 1.06;
}

.wrap {
  max-width: var(--maxw);
  margin: 0 auto;
  padding: 0 28px;
}

.eyebrow {
  color: var(--tx-faint);
  font-family: "Space Grotesk", system-ui, sans-serif;
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}

.hero {
  position: relative;
  overflow: hidden;
  padding: 96px 0 70px;
  border-bottom: 1px solid var(--line);
}

.hero-bg,
.hero-bg video,
.hero-bg img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.hero-bg video,
.hero-bg img {
  object-fit: cover;
  opacity: 0.5;
}

.hero-bg::after {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(7, 8, 12, 0.55) 0%,
    rgba(7, 8, 12, 0.78) 55%,
    var(--bg) 100%
  );
}

.hero-bg::before {
  content: "";
  position: absolute;
  z-index: 1;
  inset: 0;
  background: radial-gradient(
    120% 80% at 50% -10%,
    rgba(139, 92, 246, 0.22),
    transparent 60%
  );
}

.hero-inner {
  position: relative;
  z-index: 2;
  max-width: 880px;
  margin: 0 auto;
  padding: 0 28px;
  text-align: center;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  margin-bottom: 26px;
  padding: 7px 15px 7px 9px;
  border: 1px solid var(--line-strong);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.04);
  color: var(--tx-dim);
  font-size: 13.5px;
  font-weight: 600;
}

.hero-badge .pill {
  padding: 3px 9px;
  border-radius: 999px;
  background: linear-gradient(135deg, var(--brand), var(--brand-2));
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
}

.hero h1 {
  font-size: clamp(40px, 6vw, 74px);
  font-weight: 700;
}

.hero h1 .grad {
  background: linear-gradient(
    110deg,
    var(--brand) 10%,
    var(--brand-2) 55%,
    var(--c-audio) 95%
  );
  background-clip: text;
  color: transparent;
  -webkit-background-clip: text;
}

.hero p.sub {
  max-width: 620px;
  margin: 22px auto 0;
  color: var(--tx-dim);
  font-size: clamp(17px, 2vw, 21px);
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 11px 18px;
  border: 1px solid transparent;
  border-radius: 11px;
  font-size: 15px;
  font-weight: 700;
  white-space: nowrap;
  cursor: pointer;
  transition: 0.18s;
}

.btn-primary {
  background: linear-gradient(135deg, #7c3aed, #9d5cf7);
  color: #fff;
  box-shadow: 0 8px 22px -10px rgba(124, 58, 237, 0.8);
}

.btn-primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 12px 28px -10px rgba(124, 58, 237, 0.9);
}

.btn-line {
  border-color: var(--line-strong);
  background: rgba(255, 255, 255, 0.02);
  color: var(--tx);
}

.btn-line:hover {
  border-color: var(--line-strong);
  background: rgba(255, 255, 255, 0.07);
}

.btn-lg {
  padding: 15px 26px;
  border-radius: 13px;
  font-size: 16px;
}

.gen-modes {
  display: inline-flex;
  gap: 4px;
  margin: 30px auto 0;
  padding: 4px;
  border: 1px solid var(--line-strong);
  border-radius: 13px;
  background: var(--surface);
}

.gen-mode {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 8px 16px;
  border: 0;
  border-radius: 9px;
  background: transparent;
  color: var(--tx-dim);
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition: 0.16s;
}

.gen-mode.active {
  background: linear-gradient(135deg, #7c3aed, #9d5cf7);
  color: #fff;
  box-shadow: 0 6px 16px -8px rgba(124, 58, 237, 0.8);
}

.gm-ico {
  width: 16px;
  height: 16px;
}

.prompt-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  max-width: 660px;
  margin: 18px auto 0;
  padding: 8px 8px 8px 20px;
  border: 1px solid var(--line-strong);
  border-radius: 16px;
  background: var(--surface);
  box-shadow: 0 24px 60px -28px rgba(0, 0, 0, 0.9);
}

.prompt-bar .ico {
  flex: none;
  width: 22px;
  height: 22px;
  color: var(--brand);
}

.prompt-bar input {
  flex: 1;
  min-width: 0;
  border: 0;
  outline: none;
  background: none;
  color: var(--tx);
  font-size: 16px;
  font-weight: 500;
}

.prompt-bar input::placeholder {
  color: var(--tx-faint);
}

.hero-entries {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
  margin: 18px auto 0;
}

.hero-chip {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  padding: 9px 16px;
  border: 1px solid var(--line-strong);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.03);
  color: var(--tx-dim);
  font-size: 14px;
  font-weight: 600;
  transition: 0.18s;
  cursor: pointer;
}

.hero-chip:hover {
  border-color: var(--accent);
  background: rgba(255, 255, 255, 0.06);
  color: var(--tx);
  transform: translateY(-1px);
}

.hero-chip .dot,
.feat .dot {
  flex: none;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--accent);
}

.trustrow {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 26px;
  margin-top: 42px;
  color: var(--tx-faint);
  font-size: 13.5px;
  font-weight: 600;
}

.trustrow .sep {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: var(--tx-faint);
  opacity: 0.5;
}

section.block {
  padding: 86px 0;
}

.sec-head {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 30px;
  margin-bottom: 42px;
}

.sec-head > div {
  flex: 1 1 auto;
  min-width: 0;
}

.sec-head h2 {
  font-size: clamp(28px, 3.6vw, 44px);
}

.sec-head .lead {
  max-width: 480px;
  margin-top: 14px;
  color: var(--tx-dim);
  font-size: 16px;
}

.sec-head .eyebrow {
  display: block;
  margin-bottom: 14px;
}

.seemore {
  display: inline-flex;
  flex: none;
  align-items: center;
  gap: 7px;
  padding-bottom: 4px;
  border: 0;
  border-bottom: 2px solid var(--line-strong);
  background: transparent;
  color: var(--tx);
  font-size: 15px;
  font-weight: 700;
  white-space: nowrap;
  cursor: pointer;
  transition: 0.18s;
}

.seemore:hover {
  gap: 11px;
  border-color: var(--brand);
}

.hub {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 16px;
}

.hub-card {
  position: relative;
  display: flex;
  min-height: 230px;
  overflow: hidden;
  flex-direction: column;
  padding: 26px 24px;
  border: 1px solid var(--line);
  border-radius: var(--radius);
  background: var(--surface);
  transition: 0.2s;
  cursor: pointer;
}

.hub-card.big,
.hub-card.sm {
  grid-column: span 2;
}

.hub-card.tall {
  grid-column: span 3;
}

.hub-card:hover {
  border-color: var(--accent);
  background: var(--surface-2);
  transform: translateY(-4px);
}

.hub-card .glow {
  position: absolute;
  top: -40%;
  right: -20%;
  width: 60%;
  height: 90%;
  background: radial-gradient(circle, var(--accent), transparent 70%);
  opacity: 0.14;
  filter: blur(10px);
  pointer-events: none;
}

.hub-ico {
  display: grid;
  place-items: center;
  width: 46px;
  height: 46px;
  margin-bottom: 18px;
  border-radius: 13px;
  background: color-mix(in oklab, var(--accent) 18%, transparent);
  color: var(--accent);
}

.hub-ico :deep(svg) {
  width: 24px;
  height: 24px;
}

.hub-card > *:not(.glow) {
  position: relative;
  z-index: 2;
}

.hub-card h3 {
  margin-bottom: 8px;
  font-size: 23px;
}

.hub-card .desc {
  flex: 1;
  margin-bottom: 18px;
  color: var(--tx-dim);
  font-size: 14.5px;
}

.hub-count {
  color: var(--accent);
  font-family: "Space Grotesk", system-ui, sans-serif;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.02em;
}

.hub-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  margin-top: 14px;
}

.hub-tags a {
  padding: 5px 11px;
  border: 1px solid var(--line);
  border-radius: 999px;
  color: var(--tx-dim);
  font-size: 12.5px;
  font-weight: 600;
  white-space: nowrap;
  transition: border-color 0.2s ease, color 0.2s ease;
}

.hub-tags a:hover {
  border-color: var(--accent);
  color: var(--accent);
}

.hub-arrow {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  margin-top: 18px;
  color: var(--accent);
  font-size: 14px;
  font-weight: 700;
}

.feature-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
}

.feat,
.tool {
  display: flex;
  overflow: hidden;
  flex-direction: column;
  border: 1px solid var(--line);
  background: var(--surface);
  transition: 0.2s;
}

.feat {
  border-radius: var(--radius);
}

.feat:hover,
.tool:hover {
  border-color: var(--line-strong);
  background: var(--surface-2);
  transform: translateY(-4px);
}

.feat .media {
  position: relative;
  overflow: hidden;
  aspect-ratio: 16 / 9;
  background: #0a0c11;
}

.feat .media video,
.feat .media img,
.thumb img,
.fx-media {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.fx-media {
  position: absolute;
  z-index: 1;
  inset: 0;
  object-fit: contain;
}

.feat .body {
  padding: 22px 22px 24px;
}

.feat .tag {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  margin-bottom: 10px;
  color: var(--accent);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
}

.feat h3 {
  margin-bottom: 9px;
  font-size: 21px;
}

.feat p {
  margin-bottom: 16px;
  color: var(--tx-dim);
  font-size: 14.5px;
}

.feat .go,
.tool .try,
.fx-try {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 700;
}

.feat .go {
  color: var(--tx);
  font-size: 14.5px;
}

.feat:hover .go {
  color: var(--accent);
}

.fx-rail {
  --fx-card-h: 294px;
  display: flex;
  gap: 16px;
  overflow-x: auto;
  padding: 8px 0 16px;
  scrollbar-color: var(--line-strong) transparent;
  scrollbar-width: thin;
  scroll-snap-type: x mandatory;
}

.fx-card {
  position: relative;
  display: block;
  flex: 0 0 calc(var(--fx-card-h) * var(--cover-ratio, 0.75));
  width: calc(var(--fx-card-h) * var(--cover-ratio, 0.75));
  height: var(--fx-card-h);
  overflow: hidden;
  border: 1px solid var(--line);
  border-radius: 18px;
  background: var(--surface);
  scroll-snap-align: start;
  transition: 0.22s;
}

.fx-card:hover {
  border-color: var(--accent);
  transform: translateY(-5px);
}

.fx-bg {
  position: absolute;
  z-index: 0;
  inset: 0;
  background: linear-gradient(
    155deg,
    color-mix(in oklab, var(--fx-tag-color, var(--accent)) 55%, #0b0d13),
    #0b0d13 78%
  );
}

.fx-bg::after {
  content: "";
  position: absolute;
  inset: 0;
  background: radial-gradient(
    80% 50% at 70% 15%,
    color-mix(in oklab, var(--fx-tag-color, var(--accent)) 60%, transparent),
    transparent 70%
  );
  opacity: 0.7;
}

.fx-grain {
  position: absolute;
  inset: 0;
  opacity: 0.5;
  background-image: repeating-linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.05) 0 2px,
    transparent 2px 9px
  );
}

.fx-shade {
  position: absolute;
  z-index: 2;
  inset: 0;
  background: linear-gradient(0deg, rgba(5, 6, 9, 0.86) 4%, transparent 52%);
}

.fx-body {
  position: absolute;
  z-index: 3;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 18px 16px;
}

.fx-glyph {
  position: absolute;
  z-index: 3;
  top: 16px;
  left: 16px;
  display: grid;
  place-items: center;
  width: 38px;
  height: 38px;
  border-radius: 11px;
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  backdrop-filter: blur(6px);
}

.fx-tag {
  align-self: flex-start;
  margin-bottom: 10px;
  padding: 4px 9px;
  border-radius: 99px;
  background: color-mix(
    in oklab,
    var(--fx-tag-color, var(--accent)) 82%,
    #000 8%
  );
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
}

.fx-card h4 {
  color: #fff;
  font-size: 19px;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.fx-try {
  margin-top: 9px;
  color: #fff;
  font-size: 13px;
  opacity: 0;
  transform: translateY(6px);
  transition: 0.2s;
}

.fx-card:hover .fx-try {
  opacity: 1;
  transform: none;
}

.fx-group + .fx-group {
  margin-top: 36px;
}

.fx-grouphead {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.fx-grouphead h3 {
  display: flex;
  align-items: center;
  gap: 11px;
  font-size: 21px;
}

.fx-badge {
  display: grid;
  flex: none;
  place-items: center;
  width: 30px;
  height: 30px;
  border-radius: 9px;
  color: #fff;
}

.fx-more {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--tx-dim);
  font-size: 13.5px;
  font-weight: 700;
  white-space: nowrap;
  transition: 0.15s;
}

.fx-more:hover {
  gap: 9px;
  color: var(--brand);
}

.tools-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}

.tool {
  border-radius: 16px;
}

.tool .thumb {
  overflow: hidden;
  aspect-ratio: 16 / 9;
  background: #0a0c11;
}

.thumb-fallback {
  display: grid;
  place-items: center;
  width: 100%;
  height: 100%;
  background: radial-gradient(
    circle at top right,
    rgba(139, 92, 246, 0.35),
    #0a0c11 60%
  );
  color: var(--brand);
}

.thumb-fallback :deep(svg) {
  width: 42px;
  height: 42px;
}

.tool .t-body {
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: 16px 17px 18px;
}

.tool h4 {
  margin-bottom: 6px;
  font-size: 16.5px;
}

.tool p {
  flex: 1;
  color: var(--tx-dim);
  font-size: 13px;
  line-height: 1.45;
}

.tool .try {
  margin-top: 13px;
  color: var(--brand);
  font-size: 13.5px;
}

.models-wrap {
  border-top: 1px solid var(--line);
  border-bottom: 1px solid var(--line);
  background: var(--bg-2);
}

.model-tabs,
.uc-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 30px;
}

.model-tab,
.uc-tab {
  padding: 9px 17px;
  border: 1px solid var(--line);
  border-radius: 999px;
  background: transparent;
  color: var(--tx-dim);
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
}

.model-tab {
  transition: 0.16s;
}

.model-tab:hover {
  color: var(--tx);
}

.model-tab.active {
  border-color: var(--c-models);
  background: rgba(52, 211, 153, 0.13);
  color: var(--c-models);
}

.model-pane,
.uc-pane {
  display: none;
}

.model-pane.active,
.uc-pane.active {
  display: block;
}

.model-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
}

.model-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 17px 18px;
  border: 1px solid var(--line);
  border-radius: 14px;
  background: var(--surface);
  transition: 0.2s;
}

.model-card:hover {
  border-color: var(--c-models);
  background: var(--surface-2);
  transform: translateY(-2px);
}

.model-logo {
  display: grid;
  flex: none;
  place-items: center;
  width: 42px;
  height: 42px;
  overflow: hidden;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  font-weight: 700;
}

.model-logo img {
  width: 30px;
  height: 30px;
  object-fit: contain;
}

.model-meta {
  min-width: 0;
}

.model-meta .name,
.model-meta .prov {
  display: block;
}

.model-meta .name {
  overflow: hidden;
  color: var(--tx);
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.model-meta .prov {
  margin-top: 2px;
  color: var(--tx-faint);
  font-size: 12.5px;
  font-weight: 600;
}

.free-strip {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 14px;
}

.free-card {
  display: flex;
  align-items: center;
  gap: 13px;
  padding: 17px;
  border: 1px solid var(--line);
  border-radius: 15px;
  background: var(--surface);
  transition: 0.2s;
}

.free-card:hover {
  border-color: var(--c-free);
  background: var(--surface-2);
  transform: translateY(-2px);
}

.free-ico {
  display: grid;
  flex: none;
  place-items: center;
  width: 40px;
  height: 40px;
  border-radius: 11px;
  background: color-mix(in oklab, var(--c-free) 16%, transparent);
  color: var(--c-free);
}

.free-card .fn {
  font-weight: 600;
}

.free-card .fb {
  color: var(--tx-faint);
  font-size: 12px;
  font-weight: 600;
}

.uc {
  display: grid;
  grid-template-columns: 1.05fr 1fr;
  gap: 54px;
  align-items: center;
}

.uc-tab {
  padding: 10px 18px;
  font-size: 14.5px;
  transition: 0.16s;
}

.uc-tab:hover {
  color: var(--tx);
}

.uc-tab.active {
  border-color: var(--line-strong);
  background: rgba(255, 255, 255, 0.06);
  color: var(--tx);
}

.uc h3 {
  margin-bottom: 8px;
  font-size: 30px;
}

.uc .tagline {
  margin-bottom: 22px;
  color: var(--brand);
  font-size: 15px;
  font-weight: 700;
}

.uc-feat {
  display: flex;
  gap: 15px;
  padding: 16px 0;
  border-top: 1px solid var(--line);
}

.uc-feat:last-child {
  border-bottom: 1px solid var(--line);
}

.uc-feat .n {
  flex: none;
  width: 34px;
  color: var(--brand);
  font-family: "Space Grotesk", system-ui, sans-serif;
  font-size: 15px;
  font-weight: 700;
}

.uc-feat h4 {
  margin-bottom: 4px;
  font-size: 16.5px;
}

.uc-feat p {
  color: var(--tx-dim);
  font-size: 14px;
}

.uc-visual {
  overflow: hidden;
  aspect-ratio: 4 / 3;
  border: 1px solid var(--line);
  border-radius: var(--radius);
  background: var(--surface);
}

.uc-visual img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.tcols {
  column-count: 3;
  column-gap: 18px;
}

.tcard {
  break-inside: avoid;
  margin-bottom: 18px;
  padding: 22px;
  border: 1px solid var(--line);
  border-radius: 16px;
  background: var(--surface);
}

.stars {
  margin-bottom: 12px;
  color: var(--c-free);
  font-size: 14px;
  letter-spacing: 2px;
}

.tcard p {
  margin-bottom: 18px;
  color: var(--tx);
  font-size: 15px;
  line-height: 1.55;
}

.tcard .who {
  display: flex;
  align-items: center;
  gap: 12px;
}

.tcard .av {
  display: grid;
  flex: none;
  place-items: center;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  color: #04121a;
  font-weight: 700;
}

.tcard .nm {
  font-size: 14px;
  font-weight: 700;
}

.tcard .rl {
  color: var(--tx-faint);
  font-size: 12.5px;
  font-weight: 600;
}

.cta {
  position: relative;
  overflow: hidden;
  padding: 70px 40px;
  border: 1px solid var(--line-strong);
  border-radius: 28px;
  background: linear-gradient(180deg, var(--surface), var(--bg-2));
  text-align: center;
}

.cta::before {
  content: "";
  position: absolute;
  inset: 0;
  background: radial-gradient(
    80% 120% at 50% 0%,
    rgba(139, 92, 246, 0.22),
    transparent 60%
  );
}

.cta-in {
  position: relative;
  z-index: 1;
}

.cta h2 {
  margin-bottom: 16px;
  font-size: clamp(30px, 4vw, 50px);
}

.cta p {
  max-width: 520px;
  margin: 0 auto 30px;
  color: var(--tx-dim);
  font-size: 18px;
}

.cta strong {
  color: #c9b6ff;
  font-weight: 700;
}

.cta-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
}

@media (max-width: 1080px) {
  .hub {
    grid-template-columns: repeat(2, 1fr);
  }
  .hub-card.big,
  .hub-card.sm,
  .hub-card.tall {
    grid-column: span 1;
  }
  .tools-grid,
  .model-grid {
    grid-template-columns: repeat(3, 1fr);
  }
  .free-strip {
    grid-template-columns: repeat(3, 1fr);
  }
  .tcols {
    column-count: 2;
  }
}

@media (max-width: 820px) {
  section.block {
    padding: 60px 0;
  }
  .feature-grid,
  .uc {
    grid-template-columns: 1fr;
  }
  .fx-rail {
    --fx-card-h: 240px;
  }
  .tools-grid,
  .model-grid,
  .free-strip {
    grid-template-columns: repeat(2, 1fr);
  }
  .tcols {
    column-count: 1;
  }
}

@media (max-width: 520px) {
  .prompt-bar {
    align-items: stretch;
    flex-direction: column;
    padding: 14px;
  }
  .prompt-bar .ico {
    display: none;
  }
  .tools-grid,
  .model-grid,
  .free-strip,
  .hub {
    grid-template-columns: 1fr;
  }
}
</style>
